/**
 * Servicios Page - Editorial Style
 * Images are central to each service explanation
 * Progressive reveal on scroll
 * Alternating black/white sections for visual rhythm
 */

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageWrapper } from '../components/EditorialShared';

// Services data with images as central element
const services = [
  {
    id: 'branding',
    title: 'Branding',
    subtitle: 'Identidad que conecta',
    description: 'Creamos sistemas de identidad visual que capturan la esencia de tu marca y la comunican de manera memorable.',
    image: 'https://drive.google.com/thumbnail?id=15aukMIPeeTfvtBI1U10fCgSgB9l5HK2H&sz=w1400',
    features: ['Estrategia de marca', 'Diseño de logotipo', 'Sistema visual', 'Guía de marca'],
    caseStudy: 'Café Fantasía'
  },
  {
    id: 'fotografia',
    title: 'Fotografía',
    subtitle: 'Cada imagen cuenta una historia',
    description: 'Capturamos la esencia de tus productos y espacios con fotografía que vende, emociona y diferencia.',
    image: 'https://drive.google.com/thumbnail?id=1oCpgmt7rXyl4yFboo8GrmZyox3jgbPK-&sz=w1400',
    features: ['Fotografía gastronómica', 'Producto', 'Espacios', 'Lifestyle'],
    caseStudy: 'El Exquisito'
  },
  {
    id: 'social',
    title: 'Social Media',
    subtitle: 'Contenido que convierte',
    description: 'Estrategia, creación y gestión de contenido para redes sociales que genera engagement y resultados.',
    image: 'https://drive.google.com/thumbnail?id=1lv0noT1zD56g7FEtuEI_ZT2XcrgO6yoP&sz=w1400',
    features: ['Estrategia de contenido', 'Producción', 'Gestión de redes', 'Reportes'],
    caseStudy: 'Altitud Pádel'
  },
  {
    id: 'video',
    title: 'Video',
    subtitle: 'Movimiento que cautiva',
    description: 'Producción audiovisual que lleva tu mensaje más lejos: reels, spots, behind the scenes y más.',
    image: 'https://drive.google.com/thumbnail?id=1qDGeNYMWsXTpAARN_D0sH7tJPwQS5ddx&sz=w1400',
    features: ['Reels & Stories', 'Spots publicitarios', 'Behind the scenes', 'Edición'],
    caseStudy: 'Torii Sushi'
  },
];

// Service section - alternates between black and white backgrounds
const ServiceSection = ({ service, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-15%" });
  const isEven = index % 2 === 0;
  
  // Alternate backgrounds: first white, second black, etc.
  const isWhiteBg = index % 2 === 0;
  const bgClass = isWhiteBg ? 'bg-white' : 'bg-black';
  const textColor = isWhiteBg ? 'text-black' : 'text-white';
  const textMuted = isWhiteBg ? 'text-black/50' : 'text-white/50';
  const textLabel = isWhiteBg ? 'text-black/40' : 'text-white/40';
  const borderColor = isWhiteBg ? 'border-black/10' : 'border-white/10';

  return (
    <section 
      ref={ref} 
      className={`min-h-screen flex items-center py-24 md:py-32 ${bgClass}`}
    >
      <div className="w-full max-w-7xl mx-auto px-6">
        <div className={`grid md:grid-cols-12 gap-8 md:gap-16 items-center`}>
          {/* Image - The central element */}
          <motion.div 
            className={`md:col-span-7 ${isEven ? '' : 'md:order-2'}`}
            initial={{ opacity: 0, x: isEven ? -80 : 80 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, ease: [0.25, 0.1, 0, 1] }}
          >
            <div className="relative overflow-hidden aspect-[4/3]" data-cursor="ver caso">
              <motion.img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover"
                initial={{ scale: 1.1 }}
                animate={isInView ? { scale: 1 } : {}}
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 1 }}
                loading="lazy"
              />
              {/* Case study label */}
              <motion.div
                className={`absolute bottom-4 left-4 ${isWhiteBg ? 'bg-white/90' : 'bg-black/70'} backdrop-blur-sm px-4 py-2`}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 }}
              >
                <span className={`${textMuted} text-xs uppercase tracking-wider`}>Caso: </span>
                <span className={`${textColor} text-sm`}>{service.caseStudy}</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div 
            className={`md:col-span-5 ${isEven ? '' : 'md:order-1'}`}
            initial={{ opacity: 0, y: 60 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.span
              className={`${textLabel} text-sm uppercase tracking-widest block mb-4`}
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.3 }}
            >
              {service.subtitle}
            </motion.span>

            <motion.h2
              className={`${textColor} text-5xl md:text-7xl font-extralight mb-6`}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              {service.title}
            </motion.h2>

            <motion.p
              className={`${textMuted} text-lg leading-relaxed mb-8`}
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.5 }}
            >
              {service.description}
            </motion.p>

            <motion.ul
              className="space-y-3 mb-8"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.6 }}
            >
              {service.features.map((feature, i) => (
                <motion.li
                  key={i}
                  className={`${textMuted} flex items-center gap-3`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.6 + i * 0.1 }}
                >
                  <span className={`w-1.5 h-1.5 ${isWhiteBg ? 'bg-black/30' : 'bg-white/30'} rounded-full`} />
                  {feature}
                </motion.li>
              ))}
            </motion.ul>

            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.8 }}
            >
              <Link
                to="/casos"
                className={`${textColor} hover:opacity-50 transition-opacity inline-flex items-center gap-2`}
                data-cursor="ver más"
              >
                Ver proyectos de {service.title.toLowerCase()} →
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Hero section - BLACK background
const HeroSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section className="min-h-[70vh] flex items-end pb-24 pt-32 bg-black" ref={ref}>
      <div className="max-w-5xl mx-auto px-6">
        <motion.span
          className="text-white/40 text-sm tracking-widest uppercase mb-6 block"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3 }}
        >
          Lo que hacemos
        </motion.span>
        
        <motion.h1
          className="text-white text-5xl md:text-7xl lg:text-8xl font-extralight leading-[0.95] mb-8"
          initial={{ opacity: 0, y: 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.5 }}
        >
          Servicios
        </motion.h1>

        <motion.p
          className="text-white/50 text-xl md:text-2xl font-light max-w-2xl"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
        >
          Construimos marcas que conectan, contenido que vende, y experiencias que permanecen.
        </motion.p>
      </div>
    </section>
  );
};

// CTA Section - BLACK background
const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-20%" });

  return (
    <section className="py-32 md:py-48 bg-black" ref={ref}>
      <div className="max-w-4xl mx-auto px-6 text-center">
        <motion.h2
          className="text-white text-4xl md:text-6xl font-extralight mb-8"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          ¿Tienes un proyecto
          <br />
          en mente?
        </motion.h2>

        <motion.p
          className="text-white/50 text-lg mb-12 max-w-xl mx-auto"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.3 }}
        >
          Cada marca es única. Platiquemos sobre lo que necesitas.
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
        >
          <a
            href="https://wa.me/527774385617"
            target="_blank"
            rel="noopener noreferrer"
            className="px-10 py-5 bg-white text-black font-medium hover:bg-white/90 transition-colors"
            data-cursor="escribir"
          >
            Agendar diagnóstico gratuito
          </a>
          <Link
            to="/metodo"
            className="px-10 py-5 border border-white/20 text-white hover:bg-white/10 transition-colors"
            data-cursor="conocer"
          >
            Conocer nuestro método
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Main component
const EditorialServiciosPage = () => {
  return (
    <PageWrapper showLogoFooter={true}>
      <HeroSection />
      
      {/* Services - alternating black/white backgrounds */}
      {services.map((service, index) => (
        <ServiceSection key={service.id} service={service} index={index} />
      ))}

      <CTASection />
    </PageWrapper>
  );
};

export default EditorialServiciosPage;
